import { useState } from 'react'
import { TFunction } from 'next-i18next'
import styled from 'styled-components'

import config from '@/config'
import { breakpoints, colors } from '@/styles'
import { Button } from '@/components/button-new'
import {
  CopyIcon,
  EmailIcon,
  LinkIcon,
  MessengerIcon,
  TextIcon,
  WhatsappIcon
} from '@/components/icons'
import { createModal } from '@/components/modalize'
import { Label } from '@/components/text'

import SocialShareButton from './social-share-button'

const { BASE_URL, FACEBOOK_APP_ID } = config

const Wrapper = styled.div`
  font-size: 16px;
  line-height: 20px;
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 16px;
  line-height: 20px;
  font-weight: 700;
  padding: 24px;
  box-shadow: inset 0px -1.5px 0px rgba(0, 0, 0, 0.1);
`

const ModalContent = styled.div`
  overflow: none;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
`

const CopySection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-radius: 6px;
  gap: 16px;
  background-color: ${colors.grey96};

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column;
  }
`

const CopyLink = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: scroll;
  height: 24px;

  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`

const CopyWrapper = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  max-width: 100%;
  gap: 12px;

  svg {
    flex-shrink: 0;
  }
`

const Socials = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr 1fr 1fr 1fr;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    gap: 16px;
    grid-template-columns: 1fr 1fr;
  }
`

const CopyLinkButton = styled(Button)`
  min-width: 150px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: 100%;
  }
`

const CopyToClipboard = (str: string) => {
  if (window.isSecureContext) navigator.clipboard.writeText(str)
}

interface Props {
  url: string
  t: TFunction
}

const ShareModal: React.FC<Props> = ({ url, t }) => {
  const [copied, setCopied] = useState(false)
  // Had to encode twice because for some reason, the urls won't encode for sms, whatsapp and email if you do it only once
  const encodedUrl = encodeURI(encodeURI(url))
  const urlBodyText = `Check out this listing on liv.rent: ${encodeURIComponent(
    encodedUrl
  )}`

  return (
    <Wrapper>
      <ModalHeader>
        {t(
          'listing:b.listing.view.share_listing.modal_title',
          'Share this listing'
        )}
      </ModalHeader>
      <ModalContent>
        <CopySection>
          <CopyWrapper>
            <LinkIcon height={18} width={18} />
            <CopyLink>
              <Label fontSize="14px" type="small">
                {encodedUrl}
              </Label>
            </CopyLink>
          </CopyWrapper>
          <CopyLinkButton
            id="share-modal-copy-link-click"
            theme="secondary"
            width={150}
            size="unset"
            onClick={() => {
              CopyToClipboard(encodedUrl)
              setCopied(true)
            }}
          >
            <CopyIcon height={18} width={18} />
            {copied
              ? t(
                  'listing:b.listing.view.share_listing.link_copied',
                  'Link Copied!'
                )
              : t(
                  'listing:b.listing.view.share_listing.copy_link',
                  'Copy Link'
                )}
          </CopyLinkButton>
        </CopySection>
        <Socials>
          <SocialShareButton
            id="share-modal-messenger-click"
            href={`https://www.facebook.com/dialog/send?app_id=${FACEBOOK_APP_ID}&link=${encodedUrl}&redirect_uri=${BASE_URL}`}
            target="_blank"
            icon={<MessengerIcon height={18} width={18} />}
            title={t(
              'listing:b.listing.view.social_text.messenger',
              'Messenger'
            )}
          />
          <SocialShareButton
            id="share-modal-whatsapp-click"
            href={`https://api.whatsapp.com/send?text=${urlBodyText}`}
            target="_blank"
            icon={<WhatsappIcon height={18} width={18} />}
            title={t('listing:b.listing.view.social_text.whatsapp', 'Whatsapp')}
          />
          <SocialShareButton
            id="share-modal-text-click"
            href={`sms:&body=${urlBodyText}`}
            icon={<TextIcon height={18} width={18} />}
            title={t('listing:b.listing.view.social_text.text_message', 'Text')}
          />
          <SocialShareButton
            id="share-modal-email-click"
            href={`mailto:?subject=Check%20out%20this%20listing%20on%20liv.rent&body=${encodedUrl}`}
            icon={<EmailIcon height={18} width={18} />}
            title={t('common.email', 'Email')}
          />
        </Socials>
      </ModalContent>
    </Wrapper>
  )
}

export const show = createModal(ShareModal, {
  maxWidth: '650px',
  width: '100%',
  closeButtonTop: '24px'
})
